import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef } from 'react';

import { getDates, isDateToday, formatDate_yyyyMMdd } from '@cineamo/ui/src/helpers/date-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import { useCinemasShowingsSearch } from '../hooks/useCinemasShowingsSearch';
import { useAnalytics } from '../providers/analyticsProvider';
import { useRequiredShowtimesFinder } from '../providers/ShowtimesFinderProvider';

import { getDateButtonsStartDate, isBeforeReleaseDate, PREVIEWS_SELECTED_DATE } from './previewsUtils';

const DateButton: React.FC<{
    label: React.ReactNode;
    isSelected: boolean;
    onClick: () => void;
}> = ({ label, isSelected, onClick }) => {
    return (
        <button
            onClick={onClick}
            type="button"
            style={
                isSelected
                    ? ({
                          '--date-button-on-background-color':
                              'var(--date-button-active-color,var(--on-primary-color))',
                          '--date-button-background-color':
                              'var(--date-button-active-background-color,var(--primary-color))'
                      } as React.CSSProperties)
                    : {}
            }
            className={classnames(
                'text-[length:var(--date-button-font-size)] font-bold relative',
                'w-fit min-w-[90px] grow shrink-0 h-[64px] rounded-4 p-12 flex flex-col items-center justify-center space-x-10',
                'bg-[var(--date-button-background-color)]',
                'text-[var(--date-button-on-background-color)]'
            )}>
            {label}
        </button>
    );
};

/**
 * Pure display component for date filter buttons.
 * Receives showing counts externally — does NOT fetch data itself.
 * `showingsCounts` is an array of 8 values: [day0, day1, ..., day6, all]. Each is the number,
 * `undefined` while its request is in flight (rendered as a skeleton), or `null` when no count is
 * obtainable — no location, no cinema selected — in which case the badge is omitted instead of
 * showing a skeleton that would never resolve.
 */
export const DatesFilterDisplay: React.FC<{
    showButtons?: boolean;
    showingsCounts: (number | null | undefined)[];
    // First day of the seven day buttons; defaults to today. Callers passing a different
    // start date must derive `showingsCounts` from the same window.
    startDate?: Date;
    // When true and `previewsCount` resolves to > 0, a PREVIEWS tab is rendered
    // between ALL and the day buttons. The tab is hidden entirely when no previews exist.
    showPreviews?: boolean;
    previewsCount?: number;
}> = ({ showButtons = false, showingsCounts, startDate, showPreviews = false, previewsCount }) => {
    const { selectedDate, setSelectedDate } = useRequiredShowtimesFinder();
    const t = useTranslations();
    const { track } = useAnalytics();
    const {
        dateTime: { formatWeekday, formatDayMonth }
    } = useFormat();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { styleFadedState } = useScrollState({
        scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        orientation: 'horizontal'
    });

    const showingsCountsForDays = useMemo(() => showingsCounts.slice(0, 7), [showingsCounts]);
    const countAll = showingsCounts[7];

    const dates = useMemo(
        () =>
            getDates(7, startDate).map((date, index) => {
                const dayLabel = isDateToday(date) ? t('showtimes-finder.date.today') : formatWeekday(date, 'short');
                const dateLabel = formatDayMonth(date);
                const dateForUrl = formatDate_yyyyMMdd(date);
                return {
                    label: (
                        <span
                            key={`DateButtonContent-${dateForUrl}`}
                            className="flex flex-col">
                            <span className="absolute top-0 right-4 leading-[18px]">
                                {showingsCountsForDays[index] === null ? null : showingsCountsForDays[index] ===
                                  undefined ? (
                                    <span className="inline-block animate-pulse-skeleton bg-[color-mix(in_srgb,var(--date-button-on-background-color)_50%,transparent)] rounded-2 w-10 h-10" />
                                ) : (
                                    <span className="text-body-bold-10 opacity-50">{showingsCountsForDays[index]}</span>
                                )}
                            </span>
                            <span>{dayLabel}</span>

                            <span>{dateLabel}</span>
                        </span>
                    ),
                    date: dateForUrl
                };
            }),
        [showingsCountsForDays, startDate, formatWeekday, formatDayMonth, t]
    );

    return (
        <div className="relative">
            <div
                ref={scrollContainerRef}
                style={styleFaded(styleFadedState, 10)}
                className="w-full p-12 flex flex-row space-x-6 overflow-x-auto no-scrollbar">
                <DateButton
                    label={
                        <span
                            key="DateButtonContent-all"
                            className="flex flex-col">
                            <span className="absolute top-0 right-4 leading-[18px]">
                                {countAll === null ? null : countAll === undefined ? (
                                    <span className="inline-block animate-pulse-skeleton bg-[color-mix(in_srgb,var(--date-button-on-background-color)_50%,transparent)] rounded-2 w-10 h-10" />
                                ) : (
                                    <span className="text-body-bold-10 opacity-50">{countAll}</span>
                                )}
                            </span>
                            <span>{t('showtimes-finder.date.all')}</span>
                        </span>
                    }
                    isSelected={selectedDate === 'all'}
                    onClick={() => {
                        setSelectedDate('all');
                    }}
                />
                {showPreviews && previewsCount !== undefined && previewsCount > 0 && (
                    <DateButton
                        label={
                            <span
                                key="DateButtonContent-previews"
                                className="flex flex-col">
                                <span className="absolute top-0 right-4 leading-[18px]">
                                    <span className="text-body-bold-10 opacity-50">{previewsCount}</span>
                                </span>
                                <span>{t('showtimes-finder.date.previews')}</span>
                            </span>
                        }
                        isSelected={selectedDate === PREVIEWS_SELECTED_DATE}
                        onClick={() => {
                            setSelectedDate(PREVIEWS_SELECTED_DATE);
                            track('Date Selected', {
                                day: PREVIEWS_SELECTED_DATE
                            });
                        }}
                    />
                )}
                {dates.map((date, index) => (
                    <DateButton
                        key={`DateButton-${date.date}`}
                        label={date.label}
                        isSelected={selectedDate === date.date}
                        onClick={() => {
                            setSelectedDate(date.date);
                            track('Date Selected', {
                                day: index,
                                date: date.date
                            });
                        }}
                    />
                ))}
            </div>
            {showButtons && (
                <BackAndForthButtonOverlay
                    scrollContainerRef={scrollContainerRef}
                    buttonUnavailableStyle="disabled"
                    renderOutside
                    renderOnMobile={true}
                />
            )}
        </div>
    );
};

/**
 * Self-contained date filter that fetches nearby showing counts internally.
 * Used by consumers that only need the "nearby" data source (e.g. ShowtimesFinder, ShowtimesFinderMenu).
 */
const DatesFilter: React.FC<{ showButtons?: boolean; showPreviews?: boolean }> = ({
    showButtons = false,
    showPreviews = false
}) => {
    const { movieId, apiFilters, releaseDate, selectedDate, setSelectedDate } = useRequiredShowtimesFinder();

    // Previews only make sense while the release day still lies ahead; otherwise there is
    // no "now → day before the release date" window left to list.
    const isPreviewsEnabled = showPreviews && isBeforeReleaseDate(releaseDate);

    const { totalItems: previewsCount } = useCinemasShowingsSearch(movieId, PREVIEWS_SELECTED_DATE, apiFilters, {
        enabled: isPreviewsEnabled,
        releaseDate
    });

    // Never leave the finder stuck on an empty/unavailable PREVIEWS view — e.g. when it
    // was the configured start view but the movie has no preview showings. Fall back to ALL.
    useEffect(() => {
        if (selectedDate !== PREVIEWS_SELECTED_DATE) {
            return;
        }
        if (!isPreviewsEnabled || previewsCount === 0) {
            setSelectedDate('all');
        }
    }, [selectedDate, isPreviewsEnabled, previewsCount, setSelectedDate]);

    // While the movie is unreleased the day buttons start at the release date — everything
    // before it belongs to the PREVIEWS tab.
    const startDate = useMemo(() => getDateButtonsStartDate(releaseDate), [releaseDate]);

    const nearestDates = useMemo(() => getDates(7, startDate).map((date) => formatDate_yyyyMMdd(date)), [startDate]);

    const { totalItems: showingsCountDay0 } = useCinemasShowingsSearch(movieId, nearestDates[0], apiFilters);
    const { totalItems: showingsCountDay1 } = useCinemasShowingsSearch(movieId, nearestDates[1], apiFilters);
    const { totalItems: showingsCountDay2 } = useCinemasShowingsSearch(movieId, nearestDates[2], apiFilters);
    const { totalItems: showingsCountDay3 } = useCinemasShowingsSearch(movieId, nearestDates[3], apiFilters);
    const { totalItems: showingsCountDay4 } = useCinemasShowingsSearch(movieId, nearestDates[4], apiFilters);
    const { totalItems: showingsCountDay5 } = useCinemasShowingsSearch(movieId, nearestDates[5], apiFilters);
    const { totalItems: showingsCountDay6 } = useCinemasShowingsSearch(movieId, nearestDates[6], apiFilters);
    const { totalItems: showingsCountAll } = useCinemasShowingsSearch(movieId, 'all', apiFilters);

    const showingsCounts = useMemo(
        () => [
            showingsCountDay0,
            showingsCountDay1,
            showingsCountDay2,
            showingsCountDay3,
            showingsCountDay4,
            showingsCountDay5,
            showingsCountDay6,
            showingsCountAll
        ],
        [
            showingsCountDay0,
            showingsCountDay1,
            showingsCountDay2,
            showingsCountDay3,
            showingsCountDay4,
            showingsCountDay5,
            showingsCountDay6,
            showingsCountAll
        ]
    );

    return (
        <DatesFilterDisplay
            showButtons={showButtons}
            showingsCounts={showingsCounts}
            startDate={startDate}
            showPreviews={isPreviewsEnabled}
            previewsCount={previewsCount}
        />
    );
};

export default DatesFilter;
